import React, { useEffect, useState } from "react";
import { api, API, getToken } from "@/lib/api";
import { PageHeader, Card, Empty, Spinner } from "@/components/UIKit";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle2, XCircle, AlertTriangle, Undo2, Bell, RotateCcw } from "lucide-react";

export default function Drift() {
  return (
    <div data-testid="drift-page">
      <PageHeader title="Drift & administrasjon" subtitle="Kundeimport, varsler og reversering av handlinger" />
      <div className="grid grid-cols-1 gap-4">
        <ImportCard />
        <NotificationsCard />
        <RevertCard />
      </div>
    </div>
  );
}

function ImportCard() {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);

  const upload = async (file) => {
    setBusy(true); setPreview(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/kraftadmin/import/customers/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success(`${data.total} rader lest`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post("/kraftadmin/import/customers/commit", { entries: preview.entries, skip_duplicates: true });
      toast.success(`${data.created} kunder opprettet · ${data.skipped} hoppet over`);
      setPreview(null);
    } catch (e) { toast.error("Feilet"); }
    finally { setCommitting(false); }
  };

  return (
    <Card className="p-5" testid="import-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-heading font-medium text-zinc-100">Masseimport av kunder</div>
          <div className="text-xs text-zinc-500 mt-0.5">CSV med kolonner: type;navn;epost;telefon;adresse;postnr;sted;orgnr</div>
        </div>
        <label className="h-9 px-4 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium flex items-center gap-2 cursor-pointer">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Last opp CSV
          <input type="file" accept=".csv,.txt" className="hidden" data-testid="import-file"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
        </label>
      </div>
      {preview && (
        <div className="mt-3">
          <div className="flex items-center gap-4 text-sm mb-3">
            <span><strong>{preview.total}</strong> totalt</span>
            <span className="text-emerald-400">✓ {preview.ok} OK</span>
            <span className="text-amber-400">⚠ {preview.duplicates} duplikater</span>
            <span className="text-red-400">✗ {preview.errors} feil</span>
            <button onClick={commit} disabled={committing || preview.ok === 0}
              className="ml-auto h-8 px-4 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black text-xs font-medium flex items-center gap-2" data-testid="import-commit">
              {committing ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Importer {preview.ok} kunder
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto border border-zinc-900 rounded">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-zinc-950">
                <tr className="text-left text-[10px] uppercase text-zinc-500">
                  <th className="py-1.5 px-2">Rad</th><th className="py-1.5 px-2">Navn</th><th className="py-1.5 px-2">Type</th>
                  <th className="py-1.5 px-2">E-post</th><th className="py-1.5 px-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.entries.map((e) => (
                  <tr key={e.row} className="border-t border-zinc-900">
                    <td className="py-1 px-2 text-zinc-500">{e.row}</td>
                    <td className="py-1 px-2 text-zinc-200">{e.full_name}</td>
                    <td className="py-1 px-2 text-zinc-400">{e.kind}</td>
                    <td className="py-1 px-2 text-zinc-500">{e.email || "—"}</td>
                    <td className="py-1 px-2">
                      {e.duplicate ? <span className="text-amber-400">Duplikat: {e.duplicate.customer_number}</span>
                        : e.issues.length ? <span className="text-red-400">{e.issues.join(", ")}</span>
                        : <span className="text-emerald-400">OK</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

function NotificationsCard() {
  const [items, setItems] = useState(null);
  const load = async () => setItems((await api.get("/kraftadmin/notifications")).data);
  useEffect(() => { load(); }, []);
  const scan = async () => {
    const { data } = await api.post("/kraftadmin/notifications/scan");
    toast.success(`${data.created} nye varsler`);
    load();
  };
  const resolve = async (id) => { await api.post(`/kraftadmin/notifications/${id}/resolve`); load(); };
  return (
    <Card className="p-5" testid="notifications-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-heading font-medium text-zinc-100 flex items-center gap-2"><Bell className="h-4 w-4 text-amber-400" /> Varsler og oppgaver</div>
          <div className="text-xs text-zinc-500 mt-0.5">Automatisk detekterte problemer og oppfølginger</div>
        </div>
        <button onClick={scan} className="h-9 px-3.5 rounded-md border border-zinc-800 hover:border-emerald-500/30 text-sm flex items-center gap-2" data-testid="scan-notifications">
          <RotateCcw className="h-3.5 w-3.5" /> Skann på nytt
        </button>
      </div>
      {!items ? <Spinner /> : items.length === 0 ? <Empty title="Ingen aktive varsler" /> : (
        <div className="space-y-2">
          {items.map((n) => (
            <div key={n.id} className="flex items-center justify-between border border-zinc-900 rounded-md p-3">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                <div>
                  <div className="text-sm text-zinc-100">{n.subject}</div>
                  <div className="text-[11px] text-zinc-500">{n.kind} · {fmtDateTime(n.created_at)}</div>
                </div>
              </div>
              <button onClick={() => resolve(n.id)} className="h-8 px-3 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs hover:bg-emerald-500/20">
                Merk som løst
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function RevertCard() {
  const [items, setItems] = useState(null);
  const load = async () => setItems((await api.get("/kraftadmin/audit/reversible")).data);
  useEffect(() => { load(); }, []);
  const revert = async (id) => {
    if (!window.confirm("Er du sikker på at du vil reversere denne handlingen?")) return;
    try {
      await api.post(`/kraftadmin/audit/${id}/revert`);
      toast.success("Handling reversert");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
  };
  return (
    <Card className="p-5" testid="revert-card">
      <div className="font-heading font-medium text-zinc-100 flex items-center gap-2 mb-3"><Undo2 className="h-4 w-4 text-blue-400" /> Reverser handlinger</div>
      {!items ? <Spinner /> : items.length === 0 ? <Empty title="Ingen reverserbare handlinger" /> : (
        <div className="space-y-2">
          {items.slice(0, 15).map((a) => (
            <div key={a.id} className="flex items-center justify-between border border-zinc-900 rounded-md p-3">
              <div>
                <div className="text-sm text-zinc-100 font-mono">{a.action}</div>
                <div className="text-[11px] text-zinc-500">
                  {a.user_email} · {fmtDateTime(a.ts)} · {a.resource_type} {a.resource_id?.slice(0, 8)}
                  {a.details?.before && <> · før: <code className="text-emerald-400">{JSON.stringify(a.details.before).slice(0, 60)}</code></>}
                </div>
              </div>
              <button onClick={() => revert(a.id)} className="h-8 px-3 rounded-md bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs hover:bg-blue-500/20 flex items-center gap-1.5" data-testid={`revert-${a.id}`}>
                <Undo2 className="h-3 w-3" /> Reverser
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
